/**
 * File classifier — maps connected-drive filenames + folder paths to one of
 * the canonical data-room document types. Heuristic regex pass; covers the
 * common ~80% so the LLM fallback only runs on ambiguous files.
 */

import type {
  DataRoomDocument,
  DocumentClassificationConfidence,
} from '@/lib/state/dataroom-store';

export type DocumentType = DataRoomDocument['type'];

export interface ClassificationResult {
  type: DocumentType;
  confidence: DocumentClassificationConfidence;
  /** Which signal triggered the match (for debugging / UX explanation). */
  matchedOn?: string;
}

interface Rule {
  type: DocumentType;
  /** Strong signal — high confidence on filename match. */
  strong: RegExp;
  /** Weak signal — medium confidence (any folder/path match counts here too). */
  weak?: RegExp;
}

// Order matters — more specific types first.
const RULES: Rule[] = [
  {
    type: 'pitch_deck',
    strong: /(pitch[ _-]?deck|investor[ _-]?deck|seed[ _-]?deck|series[ _-]?[a-z][ _-]?deck|deck[ _-]?v\d+)/i,
    weak: /\b(deck|presentation|slides)\b/i,
  },
  {
    type: 'financials',
    strong: /(financial[ _-]?model|p&l|profit[ _-]?and[ _-]?loss|cash[ _-]?flow|forecast|projection|revenue[ _-]?model|unit[ _-]?economics|kpi[ _-]?dashboard)/i,
    weak: /\b(financials?|model|metrics|budget|burn|runway)\b/i,
  },
  {
    type: 'legal',
    strong: /(cap[ _-]?table|articles[ _-]?of[ _-]?incorporation|shareholders?[ _-]?agreement|safe[ _-]?(note|agreement)?|convertible[ _-]?note|term[ _-]?sheet|ip[ _-]?assignment|operating[ _-]?agreement|bylaws)/i,
    weak: /\b(legal|incorporation|sha|nda)\b/i,
  },
  {
    type: 'team',
    strong: /(team[ _-]?(profile|bio|overview)|founder[ _-]?(bio|profile|cv|resume)|org[ _-]?chart|hiring[ _-]?plan)/i,
    weak: /\b(team|founders?|cv|resume|bio)\b/i,
  },
  {
    type: 'product',
    strong: /(product[ _-]?(roadmap|spec|plan)|prd[ _-]?v?\d*|technical[ _-]?(arch|spec)|architecture[ _-]?(diagram|doc))/i,
    weak: /\b(roadmap|product|spec|prd|architecture|engineering)\b/i,
  },
  {
    type: 'market_research',
    strong: /(market[ _-]?(analysis|research|sizing)|tam[ _-]?sam[ _-]?som|competitive[ _-]?(analysis|landscape)|industry[ _-]?report)/i,
    weak: /\b(market|tam|sam|som|competitor[s]?|landscape)\b/i,
  },
];

/**
 * Classify a file by name (and optionally its folder path).
 *
 * @param fileName Bare filename including extension (e.g. "Q1_Financial_Model.xlsx")
 * @param folderPath Optional parent folder path (e.g. "Investors/Seed/Financials")
 */
export function classifyByName(
  fileName: string,
  folderPath?: string,
): ClassificationResult {
  const haystack = `${folderPath ?? ''} / ${fileName}`;

  // First pass — strong signals
  for (const rule of RULES) {
    const match = haystack.match(rule.strong);
    if (match) {
      return { type: rule.type, confidence: 'high', matchedOn: match[0] };
    }
  }

  // Second pass — weak signals (folder or filename)
  for (const rule of RULES) {
    if (!rule.weak) continue;
    const match = haystack.match(rule.weak);
    if (match) {
      return { type: rule.type, confidence: 'medium', matchedOn: match[0] };
    }
  }

  // No match → fallback to 'other' with low confidence
  return { type: 'other', confidence: 'low' };
}

/**
 * Quick label helper for UI.
 */
export function confidenceLabel(c?: DocumentClassificationConfidence): string {
  if (c === 'high') return 'Auto-detected';
  if (c === 'medium') return 'Likely';
  if (c === 'low') return 'Uncertain';
  return 'Manual';
}
